/**
 * Eigene Zähler (BE-06 Teil 3), z. B. „Bücher gelesen“: ein Wert, den Ole
 * selbst hochzählt, mit optionalem Ziel und Darstellung als Zahl oder
 * Fortschrittsbalken. Liegt als Zeile `zaehler:<id>` in der synchronisierten
 * Tabelle `oberflaeche` - daher keine neue Tabelle und keine Migration.
 */
import type { ID } from '@/domain/types';

export type ZaehlerDarstellung = 'zahl' | 'balken';

export interface Zaehler {
  /** `zaehler:<uuid>` */
  id: string;
  areaId: ID;
  name: string;
  wert: number;
  /** Ziel, auf das der Balken zeigt; ohne Ziel gibt es keinen Balken. */
  ziel?: number;
  darstellung: ZaehlerDarstellung;
  updatedAt: string;
}

export const ZAEHLER_PRAEFIX = 'zaehler:';
export const istZaehlerId = (id: string): boolean => id.startsWith(ZAEHLER_PRAEFIX);

/** Systemgrenze: Zeile aus der Datenbank bzw. vom Sync pruefen. */
export function zaehlerAus(roh: unknown): Zaehler | null {
  if (typeof roh !== 'object' || roh === null) return null;
  const r = roh as Record<string, unknown>;
  if (typeof r.id !== 'string' || !istZaehlerId(r.id) || typeof r.areaId !== 'string' || typeof r.name !== 'string') return null;
  const ziel = typeof r.ziel === 'number' && Number.isFinite(r.ziel) && r.ziel > 0 ? r.ziel : undefined;
  return {
    id: r.id, areaId: r.areaId, name: r.name,
    wert: typeof r.wert === 'number' && Number.isFinite(r.wert) ? r.wert : 0,
    ...(ziel !== undefined ? { ziel } : {}),
    darstellung: r.darstellung === 'balken' && ziel !== undefined ? 'balken' : 'zahl',
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : '',
  };
}

/** Anteil am Ziel, 0..1; `null` ohne Ziel. */
export const zaehlerAnteil = (z: Pick<Zaehler, 'wert' | 'ziel'>): number | null =>
  z.ziel ? Math.min(1, Math.max(0, z.wert / z.ziel)) : null;
